/**
 * Global Error Handler
 * 
 * Sets up global error handlers for uncaught errors and promise rejections
 */

import { captureError, captureMessage, addBreadcrumb } from './sentry';
import { trackEvent } from './analytics';
import { isChunkLoadError, reloadForChunkLoadError } from './chunkLoadRecovery';

type GlobalErrorEvent = ErrorEvent;

interface PromiseRejectionEvent extends Event {
  reason?: unknown;
  promise?: Promise<unknown>;
}

/**
 * Initialize global error handlers
 */
export function initGlobalErrorHandlers(): void {
  // Handle uncaught errors
  window.addEventListener('error', handleGlobalError);

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Handle console errors for debugging
  if (import.meta.env.DEV) {
    wrapConsoleError();
  }

  // Handle chunk loading failures (code splitting)
  handleChunkLoadErrors();

  console.log('[ErrorHandler] Global error handlers initialized');
}

/**
 * Handle global JavaScript errors
 */
function handleGlobalError(event: GlobalErrorEvent): void {
  const error = event.error || new Error(event.message || 'Unknown error');
  
  // Skip certain errors
  if (shouldIgnoreError(error)) {
    return;
  }

  // Add breadcrumb
  addBreadcrumb('Global error caught', 'error', {
    message: error.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });

  // Capture error
  captureError(error, {
    source: 'global_error_handler',
    filename: event.filename,
    line: event.lineno,
    column: event.colno,
  });

  // Track analytics
  trackEvent('global_error', {
    errorName: error.name,
    errorMessage: error.message,
    filename: event.filename,
  });

  // Log in development
  if (import.meta.env.DEV) {
    console.error('[GlobalError]', error);
  }
}

/**
 * Handle unhandled promise rejections
 */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;
  
  // Convert reason to Error if it isn't one
  const error = reason instanceof Error 
    ? reason 
    : new Error(String(reason) || 'Unhandled promise rejection');

  // Skip certain errors
  if (shouldIgnoreError(error)) {
    return;
  }

  // Add breadcrumb
  addBreadcrumb('Unhandled promise rejection', 'error', {
    message: error.message,
  });

  // Capture error
  captureError(error, {
    source: 'unhandled_rejection',
    originalReason: typeof reason === 'string' ? reason : undefined,
  });

  // Track analytics
  trackEvent('unhandled_rejection', {
    errorName: error.name,
    errorMessage: error.message,
  });

  // Wave A9.25: Emit a global plugin runtime error event so that
  // PluginPageHost error boundaries can pick up async failures.
  // The error boundary filters by pluginId, so this is a best-effort
  // broadcast — only the active plugin boundary will act on it.
  try {
    window.dispatchEvent(
      new CustomEvent('neuro:global-unhandled-rejection', {
        detail: {
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
          timestamp: new Date().toISOString(),
        },
      }),
    );
  } catch {
    // Best-effort — don't let telemetry break error handling
  }

  // Log in development
  if (import.meta.env.DEV) {
    console.error('[UnhandledRejection]', reason);
  }

  // Prevent default browser error logging
  event.preventDefault?.();
}

/**
 * Determine if an error should be ignored
 */
function shouldIgnoreError(error: Error): boolean {
  const message = error.message?.toLowerCase() || '';
  const name = error.name || '';

  // Ignore common non-actionable errors
  const ignorePatterns = [
    // Network errors
    'network error',
    'failed to fetch',
    'net::err_',
    'networkerror',
    
    // Cancelled requests
    'aborted',
    'cancelled',
    'canceled',
    
    // Script errors from cross-origin scripts
    'script error',
    
    // ResizeObserver errors (usually harmless)
    'resizeobserver',
    
    // Browser extensions
    'extension',
    'chrome-extension',
    'moz-extension',
    
    // Specific third-party errors
    'gtm.js',
    'analytics',
    'facebook',
  ];

  return ignorePatterns.some(pattern => 
    message.includes(pattern) || name.toLowerCase().includes(pattern)
  );
}

/**
 * Wrap console.error to capture logged errors
 */
function wrapConsoleError(): void {
  const originalError = console.error.bind(console);
  
  console.error = (...args: unknown[]) => {
    // Call original console.error
    originalError(...args);

    // Extract error if present
    const error = args.find(arg => arg instanceof Error);
    if (error) {
      addBreadcrumb('Console error', 'console', {
        message: (error as Error).message,
      });
    }
  };
}

/**
 * Handle chunk loading failures (dynamic imports)
 */
function handleChunkLoadErrors(): void {
  // Dynamic import() failures don't fire a <script> error event — Vite's
  // preload helper reports them here instead. After a deploy these are chunks
  // the previous build referenced, so reload onto the current build.
  window.addEventListener('vite:preloadError', (event) => {
    const error = (event as Event & { payload?: unknown }).payload;
    if (!isChunkLoadError(error)) return;
    captureMessage('Stale chunk after deploy', 'warning', {
      message: error instanceof Error ? error.message : String(error),
      currentUrl: window.location.href,
    });
    if (!reloadForChunkLoadError(error)) {
      showChunkErrorMessage();
    }
  });

  // Create a handler for chunk load failures
  window.addEventListener('error', (event) => {
    if (event.target instanceof HTMLScriptElement) {
      const src = event.target.src;
      
      if (src && (src.includes('/assets/') || src.includes('.js'))) {
        // This is likely a chunk loading failure
        captureMessage('Chunk load failure detected', 'warning', {
          src,
          currentUrl: window.location.href,
        });

        // Show user-friendly message and offer reload
        showChunkErrorMessage();
      }
    }
  }, true);
}

/**
 * Show message for chunk loading failures
 */
function showChunkErrorMessage(): void {
  // Check if we've already shown the message
  if (document.getElementById('chunk-error-message')) {
    return;
  }

  const container = document.createElement('div');
  container.id = 'chunk-error-message';
  container.innerHTML = `
    <div style="
      position: fixed;
      bottom: 20px;
      right: 20px;
      max-width: 360px;
      padding: 16px;
      background: #fef3c7;
      border: 1px solid #f59e0b;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
    ">
      <div style="display: flex; align-items: flex-start; gap: 12px;">
        <span style="font-size: 24px;">⚠️</span>
        <div>
          <h4 style="margin: 0 0 8px; font-size: 14px; font-weight: 600; color: #92400e;">
            Update Available
          </h4>
          <p style="margin: 0 0 12px; font-size: 13px; color: #78350f; line-height: 1.4;">
            A new version of the app is available. Please refresh to continue.
          </p>
          <button 
            onclick="window.location.reload()" 
            style="
              padding: 8px 16px;
              background: #f59e0b;
              color: white;
              border: none;
              border-radius: 6px;
              font-size: 13px;
              font-weight: 500;
              cursor: pointer;
            "
          >
            Refresh Now
          </button>
        </div>
        <button 
          onclick="this.parentElement.parentElement.remove()"
          style="
            position: absolute;
            top: 8px;
            right: 8px;
            background: none;
            border: none;
            font-size: 18px;
            color: #92400e;
            cursor: pointer;
            padding: 4px;
          "
        >×</button>
      </div>
    </div>
  `;

  document.body.appendChild(container);
}

/**
 * Report errors with context
 */
export function reportError(
  error: Error | unknown,
  context?: Record<string, unknown>
): void {
  const err = error instanceof Error ? error : new Error(String(error));
  
  captureError(err, {
    ...context,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  });

  if (import.meta.env.DEV) {
    console.error('[ReportedError]', err, context);
  }
}

/**
 * Create a safe wrapper for async functions
 */
export function withErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  context?: Record<string, unknown>
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      reportError(error, {
        ...context,
        functionName: fn.name || 'anonymous',
        args: args.length > 0 ? JSON.stringify(args).slice(0, 500) : undefined,
      });
      throw error;
    }
  }) as T;
}

/**
 * Cleanup function for tests
 */
export function cleanupGlobalErrorHandlers(): void {
  window.removeEventListener('error', handleGlobalError);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
}
